import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * How often to ask, while the page is actually being looked at.
 *
 * <p>Ten minutes, and not a minute, because nothing here is urgent: the worst
 * case is working a little longer on a build that is one fix behind. A tab left
 * open on a phone in a pocket for a week would otherwise have made thousands of
 * requests for a file that changes twice a month.
 */
const EVERY = 10 * 60 * 1000

/**
 * Quietly puts the tab onto the newest build, the next time the reader leaves
 * a screen.
 *
 * <p>This exists because of a real afternoon: a fix went out, and the shop kept
 * seeing the old screens. Nothing was wrong with the deploy. The page had simply
 * been open since before it, and a tab that already holds its own JavaScript has
 * no reason to go and look for newer JavaScript. On a phone that is never closed,
 * that tab can be days old.
 *
 * <p><b>Nothing is shown and nothing is asked.</b> A notice with a refresh button
 * was the first attempt, and it was the wrong one: whose problem a stale tab is
 * matters. It is mine. The shopkeeper did not cause it, cannot judge when to act
 * on it, and should not have to learn what a version is in order to sell flowers.
 *
 * <p>So the moment to do it is the moment he taps to another screen. He has
 * already left what he was doing, so there is nothing half-typed to lose - that
 * is the one thing reloading must never do, because a deploy lands in the middle
 * of a working morning, which is exactly when a weight and a rate are sitting in
 * a form unsaved. All he sees is that one screen change took a second longer.
 *
 * <p>The check is a single small file written at build time - see
 * vite.config.ts. Deliberately not the API: whether the browser is holding a
 * stale app is a question about the app, and the answer must not depend on being
 * signed in, on a role, or on any route the server might start refusing.
 */
export function useLatestBuild(): void {
  const { pathname } = useLocation()

  // A ref and not state on purpose: learning that a new build exists must not
  // itself redraw the screen somebody is working on. It only has to be
  // remembered until they next move.
  const behind = useRef(false)
  const settled = useRef(false)

  useEffect(() => {
    let dropped = false

    const check = async () => {
      if (document.visibilityState !== 'visible') return
      try {
        // The query string is the part that matters. index.html is served
        // no-cache but this file is new, and a phone that had cached it once
        // would answer every later check from its own disk - the check would
        // then be permanently certain that nothing had changed.
        const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
        if (!response.ok || dropped) return

        const body = (await response.json()) as { build?: unknown }
        if (typeof body.build === 'string') behind.current = body.build !== __BUILD_ID__
      } catch {
        // Offline, or the dev server answering with index.html, which is not
        // JSON. Neither is worth a word on screen: the app in front of the
        // reader works, and all that is unknown is whether a better one exists.
      }
    }

    // Waking up is the honest moment to look - it is also the moment somebody
    // has just picked the phone up to use it. Checking a hidden tab is work
    // nobody asked for, and phones freeze timers in backgrounded pages anyway.
    const onWake = () => void check()

    void check()
    const timer = setInterval(onWake, EVERY)
    document.addEventListener('visibilitychange', onWake)

    return () => {
      dropped = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onWake)
    }
  }, [])

  useEffect(() => {
    // The first run is this screen arriving, not the reader leaving one.
    if (!settled.current) {
      settled.current = true
      return
    }

    if (behind.current) window.location.reload()
  }, [pathname])
}
